// src/modules/EngenhariaAplicada/components/ContactFormModal.js
import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaTimes, FaUser, FaEnvelope, FaPhone, FaBuilding, FaFileAlt, FaPaperPlane, FaCheck } from 'react-icons/fa';

// ===== CORES =====
const colors = {
  background: '#0D0D0D',
  backgroundCard: '#161616',
  primary: '#00A8E8',
  primaryDark: '#0077B6',
  success: '#2ECC71',
  error: '#E74C3C',
  white: '#FFFFFF',
  textPrimary: '#F5F5F5',
  textSecondary: '#B8B8B8',
  textMuted: '#888888',
  border: 'rgba(255, 255, 255, 0.1)',
  borderHover: 'rgba(0, 168, 232, 0.4)'
};

// ===== ANIMAÇÕES =====
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { 
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to { 
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const checkmark = keyframes`
  0% { transform: scale(0); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
`;

// ===== STYLED COMPONENTS =====
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  animation: ${fadeIn} 0.3s ease;
`;

const Modal = styled.div`
  background: ${colors.backgroundCard};
  border: 1px solid ${colors.border};
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  animation: ${slideUp} 0.4s ease;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${colors.background};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${colors.primary};
    border-radius: 3px;
  }
`;

const ModalHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid ${colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background: ${colors.backgroundCard};
  z-index: 10;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${colors.textPrimary};
  margin: 0;
  
  span {
    color: ${colors.primary};
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${colors.textMuted};
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
  
  &:hover {
    color: ${colors.textPrimary};
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${colors.textSecondary};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    color: ${colors.primary};
    font-size: 0.9rem;
  }
`;

const Input = styled.input`
  background: ${colors.background};
  border: 1px solid ${colors.border};
  border-radius: 8px;
  padding: 0.85rem 1rem;
  font-size: 0.95rem;
  color: ${colors.textPrimary};
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${colors.primary};
    box-shadow: 0 0 0 3px rgba(0, 168, 232, 0.15);
  }
  
  &::placeholder {
    color: ${colors.textMuted};
  }
`;

const TextArea = styled.textarea`
  background: ${colors.background};
  border: 1px solid ${colors.border};
  border-radius: 8px;
  padding: 0.85rem 1rem;
  font-size: 0.95rem;
  color: ${colors.textPrimary};
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${colors.primary};
    box-shadow: 0 0 0 3px rgba(0, 168, 232, 0.15);
  }
  
  &::placeholder {
    color: ${colors.textMuted};
  }
`;

const SubmitButton = styled.button`
  background: ${colors.primary};
  color: ${colors.white};
  border: none;
  border-radius: 8px;
  padding: 1rem 1.5rem;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  margin-top: 0.5rem;
  
  &:hover:not(:disabled) {
    background: ${colors.primaryDark};
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 168, 232, 0.35);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid ${colors.white};
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const SuccessMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  gap: 1rem;
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  background: ${colors.success};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${checkmark} 0.5s ease;
  
  svg {
    color: ${colors.white};
    font-size: 2.5rem;
  }
`;

const SuccessTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${colors.textPrimary};
  margin: 0;
`;

const SuccessText = styled.p`
  font-size: 0.95rem;
  color: ${colors.textSecondary};
  margin: 0;
  line-height: 1.5;
`;

const ErrorMessage = styled.p`
  color: ${colors.error};
  font-size: 0.85rem;
  margin: 0;
  padding: 0.75rem;
  background: rgba(231, 76, 60, 0.1);
  border-radius: 8px;
  text-align: center;
`;

const PrivacyText = styled.p`
  font-size: 0.75rem;
  color: ${colors.textMuted};
  text-align: center;
  margin: 0.5rem 0 0 0;
  line-height: 1.4;
`;

// ===== COMPONENTE =====
const ContactFormModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    empresa: '',
    mensagem: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  // Fecha modal com ESC
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Reset ao fechar
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setFormData({ nome: '', email: '', telefone: '', empresa: '', mensagem: '' });
        setIsSuccess(false);
        setError('');
      }, 300);
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Validação básica
    if (!formData.nome || !formData.email || !formData.telefone) {
      setError('Por favor, preencha todos os campos obrigatórios.');
      setIsSubmitting(false);
      return;
    }

    try {
      // Enviar para HubSpot
      // Campos padrão do HubSpot: firstname, email, phone, company, mensagem_formulario
      const hubspotData = {
        fields: [
          { name: 'firstname', value: formData.nome },
          { name: 'email', value: formData.email },
          { name: 'phone', value: formData.telefone },
          { name: 'company', value: formData.empresa },
          { name: 'mensagem_formulario', value: formData.mensagem }
        ],
        context: {
          pageUri: window.location.href,
          pageName: 'CADPro - Engenharia Aplicada'
        }
      };

      const response = await fetch(
        'https://api.hsforms.com/submissions/v3/integration/submit/50764884/75e376ed-bef0-449d-a656-2ee74aa74ba0',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(hubspotData)
        }
      );

      if (response.ok) {
        setIsSuccess(true);
      } else {
        throw new Error('Erro ao enviar formulário');
      }
    } catch (err) {
      console.error('Erro:', err);
      setError('Ocorreu um erro ao enviar. Tente novamente ou entre em contato pelo WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <Modal>
        <ModalHeader>
          <ModalTitle>Solicitar <span>Orçamento</span></ModalTitle>
          <CloseButton onClick={onClose} aria-label="Fechar">
            <FaTimes />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {isSuccess ? (
            <SuccessMessage>
              <SuccessIcon>
                <FaCheck />
              </SuccessIcon>
              <SuccessTitle>Mensagem Enviada!</SuccessTitle>
              <SuccessText>
                Recebemos sua solicitação e entraremos em contato em até 24 horas úteis.
              </SuccessText>
              <SubmitButton onClick={onClose} style={{ marginTop: '1rem' }}>
                Fechar
              </SubmitButton>
            </SuccessMessage>
          ) : (
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label><FaUser /> Nome *</Label>
                <Input
                  type="text"
                  name="nome"
                  placeholder="Seu nome completo"
                  value={formData.nome}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label><FaEnvelope /> E-mail *</Label>
                <Input
                  type="email"
                  name="email"
                  placeholder="seu@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label><FaPhone /> Telefone *</Label>
                <Input
                  type="tel"
                  name="telefone"
                  placeholder="(11) 99999-9999"
                  value={formData.telefone}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label><FaBuilding /> Empresa</Label>
                <Input
                  type="text"
                  name="empresa"
                  placeholder="Nome da empresa (opcional)"
                  value={formData.empresa}
                  onChange={handleChange}
                />
              </FormGroup>

              <FormGroup>
                <Label><FaFileAlt /> Descreva seu projeto</Label>
                <TextArea
                  name="mensagem"
                  placeholder="Conte um pouco sobre o projeto que precisa desenvolver..."
                  value={formData.mensagem}
                  onChange={handleChange}
                />
              </FormGroup>

              {error && <ErrorMessage>{error}</ErrorMessage>}

              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Spinner />
                ) : (
                  <>
                    <FaPaperPlane /> Enviar Solicitação
                  </>
                )}
              </SubmitButton>

              <PrivacyText>
                Ao enviar, você concorda com nossa política de privacidade. 
                Seus dados estão seguros e não serão compartilhados.
              </PrivacyText>
            </Form>
          )}
        </ModalBody>
      </Modal>
    </Overlay>
  );
};

export default ContactFormModal;